/**
 * LedgerAuditButton — top-of-dashboard shortcut that lets the founder
 * pick any student and instantly audit how they earned their Scholar Dollars.
 *
 * Answers: "Where did this kid's 5,170 SD come from?"
 *   → per-type breakdown (worksheet_perfect, combine_set_focus, teacher_grant, redeemed…)
 *   → recent 200 ledger entries with timestamps + reason + who granted.
 *
 * Backend: GET /api/academy/admin/student-ledger/{student_id}
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Coins, Eye, Loader2, Search, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const LEDGER_LABELS = {
  worksheet_perfect: 'Worksheet 100%',
  worksheet_high: 'Worksheet 80–99%',
  combine_set_focus: 'Combine · Target Weakness',
  combine_set_all: 'Combine · All-Around',
  combine_on_fire: 'Combine · On Fire streak',
  teacher_grant: 'Admin Grant',
  lchaim_lesson: "L'Chaim Lesson",
  solo_lesson: 'SOLO Lesson',
  elective_lesson: 'Elective Lesson',
  redeemed: 'Redeemed (spent)',
};
const labelFor = (t) => LEDGER_LABELS[t] || (t || 'unknown').replace(/_/g, ' ');
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || '—'; } };

const LedgerAuditButton = () => {
  const [open, setOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    if (!open || students.length) return;
    api.get('/academy/admin/students-lite', { headers: authHeaders() })
      .then((r) => setStudents(r.data.students || []))
      .catch(() => { /* silent — teacher may lack perms */ });
  }, [open, students.length]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return students;
    return students.filter((s) =>
      (s.name || '').toLowerCase().includes(term) ||
      (s.email || '').toLowerCase().includes(term)
    );
  }, [q, students]);

  const runAudit = async (studentId) => {
    if (!studentId) return;
    setSelected(studentId);
    setLoading(true);
    setAudit(null);
    try {
      const r = await api.get(`/academy/admin/student-ledger/${studentId}`, { headers: authHeaders() });
      setAudit(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load ledger');
    } finally {
      setLoading(false);
    }
  };

  const close = () => { setOpen(false); setAudit(null); setSelected(''); setQ(''); };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 border-2 border-yellow-500 bg-yellow-500 hover:bg-yellow-400 text-black px-4 py-2 text-[10px] font-black uppercase tracking-widest"
        data-testid="link-ledger-audit"
      >
        <Eye className="w-3.5 h-3.5" /> Ledger Audit
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85"
          onClick={close}
          data-testid="ledger-audit-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-zinc-950 border-2 border-yellow-500/60"
          >
            {/* Header */}
            <div className="sticky top-0 bg-zinc-950 border-b-2 border-yellow-500/40 px-5 py-3 flex items-center justify-between z-10">
              <div className="flex items-center gap-2 text-yellow-400">
                <Coins className="w-4 h-4" />
                <span className="text-[10px] font-black uppercase tracking-[0.28em]">Scholar Dollar Ledger Audit</span>
              </div>
              <button onClick={close} className="text-zinc-400 hover:text-white p-1" data-testid="ledger-audit-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Student picker */}
              {!audit && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">
                    Pick a student to audit
                  </div>
                  <div className="relative mb-3">
                    <Search className="w-3.5 h-3.5 text-zinc-600 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search name or email…"
                      autoFocus
                      className="w-full bg-black border-2 border-zinc-800 pl-8 pr-3 py-2 text-sm text-white outline-none focus:border-yellow-500"
                      data-testid="ledger-audit-search"
                    />
                  </div>
                  <div className="border-2 border-zinc-800 divide-y divide-zinc-800 max-h-96 overflow-y-auto">
                    {filtered.length === 0 && (
                      <div className="px-3 py-4 text-xs text-zinc-500 italic">
                        {students.length === 0 ? 'Loading roster…' : 'No students match that search.'}
                      </div>
                    )}
                    {filtered.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => runAudit(s.id)}
                        className="w-full text-left px-3 py-2 hover:bg-yellow-500/5 flex items-center justify-between gap-3"
                        data-testid={`ledger-audit-pick-${s.id}`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm text-white font-bold truncate">{s.name}</div>
                          <div className="text-[10px] text-zinc-500 truncate">{s.email} · G{s.grade_level ?? '?'}</div>
                        </div>
                        <div className="text-yellow-300 font-black text-sm tabular-nums flex-shrink-0">
                          {s.balance ?? 0} SD
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Loading */}
              {loading && (
                <div className="flex items-center justify-center py-12 text-zinc-500 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading ledger…
                </div>
              )}

              {/* Audit result */}
              {audit && !loading && (
                <>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-lg font-black text-white">{audit.student?.name}</div>
                      <div className="text-[11px] text-zinc-500">
                        {audit.student?.email} · G{audit.student?.grade_level ?? '?'}
                      </div>
                    </div>
                    <button
                      onClick={() => { setAudit(null); setSelected(''); }}
                      className="text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-yellow-300 border border-zinc-700 hover:border-yellow-500 px-2 py-1"
                      data-testid="ledger-audit-pick-another"
                    >
                      Audit Another
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="border-2 border-yellow-500/40 bg-yellow-500/5 p-3">
                      <div className="text-[9px] font-black uppercase tracking-widest text-yellow-400">Balance</div>
                      <div className="text-2xl font-black text-white">{audit.balance?.balance ?? 0}</div>
                    </div>
                    <div className="border-2 border-emerald-500/30 bg-emerald-500/5 p-3">
                      <div className="text-[9px] font-black uppercase tracking-widest text-emerald-400">Total Earned</div>
                      <div className="text-2xl font-black text-white">{audit.totals?.earned ?? 0}</div>
                    </div>
                    <div className="border-2 border-red-500/30 bg-red-500/5 p-3">
                      <div className="text-[9px] font-black uppercase tracking-widest text-red-400">Total Spent</div>
                      <div className="text-2xl font-black text-white">{audit.totals?.spent ?? 0}</div>
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">
                      How they earned it
                    </div>
                    {(audit.breakdown || []).length === 0 ? (
                      <div className="text-xs text-zinc-500 italic">No ledger entries yet.</div>
                    ) : (
                      <div className="border-2 border-zinc-800 divide-y divide-zinc-800">
                        {audit.breakdown.map((b) => (
                          <div key={b.type} className="flex items-center justify-between px-3 py-2 text-sm">
                            <div>
                              <div className="font-bold text-white">{labelFor(b.type)}</div>
                              <div className="text-[10px] text-zinc-500">
                                {b.count}× · first {fmtDate(b.first_at)} · last {fmtDate(b.last_at)}
                              </div>
                            </div>
                            <div className={`font-black tabular-nums ${b.total < 0 ? 'text-red-400' : 'text-yellow-300'}`}>
                              {b.total > 0 ? '+' : ''}{b.total}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">
                      Recent worksheet submissions ({audit.submissions_shown || 0})
                      <span className="ml-2 normal-case tracking-normal text-zinc-600">
                        · <b className="text-emerald-400">Locked</b> = post-fix (can't retry)
                        · <b className="text-red-400">Unlocked</b> = pre-fix era (retry was possible)
                      </span>
                    </div>
                    <div className="border-2 border-zinc-800 divide-y divide-zinc-800 max-h-60 overflow-y-auto">
                      {(audit.submissions || []).length === 0 && (
                        <div className="px-3 py-3 text-xs text-zinc-500 italic">No worksheet submissions yet.</div>
                      )}
                      {(audit.submissions || []).map((s, i) => {
                        const fast = s.gap_seconds !== null && s.gap_seconds < 60;
                        return (
                          <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-xs">
                            <div className="min-w-0 flex-1">
                              <div className="font-bold text-white">
                                {s.date} · Grade {s.grade}
                              </div>
                              <div className="text-[10px] text-zinc-500">
                                Submitted {fmtDate(s.submitted_at)}
                                {s.gap_seconds !== null && (
                                  <> · draft→submit gap <span className={fast ? 'text-red-400 font-black' : 'text-zinc-400'}>
                                    {s.gap_seconds}s
                                    {fast ? ' ⚠️ FAST' : ''}
                                  </span></>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={`px-1.5 py-0.5 text-[9px] font-black tracking-widest uppercase border ${s.locked ? 'text-emerald-400 border-emerald-500/40' : 'text-red-400 border-red-500/40'}`}>
                                {s.locked ? 'Locked' : 'Unlocked'}
                              </span>
                              <span className={`font-black tabular-nums ${s.score === 100 ? 'text-yellow-300' : 'text-white'}`}>
                                {s.correct}/{s.total} · {s.score}%
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">
                      Recent transactions ({audit.entries_shown})
                    </div>
                    <div className="border-2 border-zinc-800 divide-y divide-zinc-800 max-h-80 overflow-y-auto">
                      {(audit.entries || []).map((e, i) => (
                        <div key={i} className="flex items-start justify-between gap-3 px-3 py-2 text-xs">
                          <div className="min-w-0 flex-1">
                            <div className="font-bold text-white">{labelFor(e.type)}</div>
                            <div className="text-[10px] text-zinc-500 truncate">
                              {fmtDate(e.created_at)}
                              {e.reason ? ` · ${e.reason}` : ''}
                              {e.granted_by ? ` · by ${e.granted_by}` : ''}
                            </div>
                          </div>
                          <div className={`font-black tabular-nums flex-shrink-0 ${e.xp < 0 ? 'text-red-400' : 'text-yellow-300'}`}>
                            {e.xp > 0 ? '+' : ''}{e.xp}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LedgerAuditButton;
